import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, Flame, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { COMPANY, NAV_LINKS } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white border-b-2 border-slate-200 shadow-[0_1px_0_0_rgba(15,23,42,0.04)]"
    >
      {/* Top utility strip */}
      <div className="bg-brand-navy text-white text-xs">
        <div className="container-x flex items-center justify-between py-2 gap-4">
          <div className="flex items-center gap-2 font-semibold tracking-wide">
            <ShieldCheck className="w-4 h-4 text-brand-orange" />
            <span>Gas Safe Registered · No. {COMPANY.gasSafeNumber}</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-slate-200">
            <span className="opacity-80">{COMPANY.address}</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="container-x flex items-center justify-between py-4 gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <span className="grid place-items-center w-11 h-11 bg-brand-navy text-white rounded-sm">
            <Flame className="w-6 h-6 text-brand-orange" />
          </span>
          <span className="leading-tight">
            <span className="block font-heading font-black text-xl tracking-tight text-brand-navy">
              EL-Gas <span className="text-brand-orange">&amp;</span> Plumbing
            </span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
              Lower Clapton · London E5
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-bold tracking-wide uppercase transition-colors rounded-sm ${
                  isActive
                    ? "text-brand-orange"
                    : "text-slate-700 hover:text-brand-navy"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="header-call-now-button"
            className="hidden sm:inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDeep text-white px-5 py-3 font-bold rounded-sm shadow-sm transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden md:inline">Call Now:</span>
            <span>{COMPANY.phone}</span>
          </a>
          <button
            className="lg:hidden p-2 text-brand-navy"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="container-x py-4 flex flex-col">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `px-2 py-3 text-base font-bold uppercase tracking-wide border-b border-slate-100 ${
                    isActive ? "text-brand-orange" : "text-slate-800"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={`tel:${COMPANY.phoneTel}`}
              className="btn-primary mt-4"
              data-testid="mobile-call-now-button"
            >
              <Phone className="w-4 h-4" /> Call Now: {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
